import React, { useState, useEffect } from 'react';
import { ShieldCheckIcon, StarIcon, ChartBarIcon, HomeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';

interface VerificationPromptProps {
  user: any;
  onDismiss?: () => void;
  onStartVerification: () => void;
  className?: string;
}

const VerificationPrompt: React.FC<VerificationPromptProps> = ({
  user,
  onDismiss,
  onStartVerification,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [verificationData, setVerificationData] = useState<any>(null);

  console.log(`🔔 Rendering verification prompt for teacher ${user?.email}`);

  useEffect(() => {
    if (user?.role === 'teacher') {
      fetchVerificationStatus();
    }
  }, [user]);

  const fetchVerificationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/verification/status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVerificationData(data);
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    if (onDismiss) {
      onDismiss();
    }
  };

  // Don't show if already verified or dismissed
  if (!isVisible || !user || user.role !== 'teacher' || verificationData?.verification?.isVerified) {
    return null;
  }

  const isPending = verificationData?.verification?.status === 'pending';
  const backgroundCheckStatus = verificationData?.verification?.backgroundCheckStatus;

  if (isPending) {
    return (
      <div className={`bg-blue-50 border border-blue-200 rounded-lg p-6 ${className}`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-medium text-blue-900">
              🔍 Verification in Progress
            </h3>
            <p className="mt-1 text-sm text-blue-700">
              Your background check is being processed. We'll notify you when it's complete!
            </p>
            <div className="mt-3 text-xs text-blue-600">
              Status: {backgroundCheckStatus === 'pending' ? 'Processing background check' : 'Awaiting payment confirmation'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
        </div>
        
        <div className="ml-4 flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              Get Verified & Unlock Premium Features
              <CheckBadgeIcon className="ml-2 h-5 w-5 text-blue-500" />
            </h3>
            {onDismiss && (
              <button
                onClick={handleDismiss}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          
          <p className="mt-2 text-sm text-gray-600">
            Stand out from other teachers and earn more with background verification. 
            <span className="font-medium text-blue-600"> Only $29.99</span> for one year.
          </p>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start space-x-2">
              <HomeIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-900">In-Home Lessons</div>
                <div className="text-xs text-gray-500">Teach at student homes</div>
              </div>
            </div>
            
            <div className="flex items-start space-x-2">
              <ChartBarIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-900">Higher Visibility</div>
                <div className="text-xs text-gray-500">Rank higher in search</div>
              </div>
            </div>
            
            <div className="flex items-start space-x-2">
              <StarIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-gray-900">Trust Badge</div>
                <div className="text-xs text-gray-500">Blue verification checkmark</div>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Background Check Includes:
            </h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>✓ Criminal history screening</li>
              <li>✓ Identity verification</li>
              <li>✓ Sex offender registry check</li>
              <li>✓ National security database check</li>
            </ul>
          </div>

          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-4 w-4 text-yellow-600 mt-0.5" />
              </div>
              <div className="ml-2">
                <p className="text-xs text-yellow-800">
                  <span className="font-medium">Verified teachers earn 40% more</span> and get 3x more bookings on average.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={onStartVerification}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Get Verified Now - $29.99
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Maybe Later
            </button>
          </div>

          <p className="mt-2 text-xs text-gray-500 text-center">
            🔒 Secure payment • 2-5 business day processing • Valid for 1 year
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerificationPrompt; 